import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GripVertical, Edit, ToggleLeft, ToggleRight } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { BookingType } from '@/types';

const iconMap: Record<string, string> = {
  utensils: '🍽️',
  ticket: '🎫',
  film: '🎬',
  bed: '🛏️',
  building: '🏢',
  clipboard: '📋',
};

export default function BookingTypesList() {
  const queryClient = useQueryClient();

  const { data: bookingTypes, isLoading } = useQuery<BookingType[]>({
    queryKey: ['booking-types'],
    queryFn: async () => {
      const response = await api.get('/admin/booking-types');
      return response.data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      await api.put(`/admin/booking-types/${id}`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-types'] });
    },
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Types</h1>
          <p className="text-gray-500 mt-1">
            Configure the types of bookings available on the platform
          </p>
        </div>
      </div>

      <div className="card">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="w-10"></div>
            <div className="flex-1 px-4">Booking Type</div>
            <div className="w-32 text-center">Venues Using</div>
            <div className="w-24 text-center">Status</div>
            <div className="w-20 text-right">Actions</div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {isLoading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="p-4 flex items-center animate-pulse">
                <div className="w-10 h-6 bg-gray-200 rounded"></div>
                <div className="flex-1 px-4">
                  <div className="h-5 bg-gray-200 rounded w-40 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-64"></div>
                </div>
                <div className="w-32 flex justify-center">
                  <div className="h-4 bg-gray-200 rounded w-8"></div>
                </div>
                <div className="w-24 flex justify-center">
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="w-20"></div>
              </div>
            ))
          ) : (
            bookingTypes?.map((type) => (
              <div
                key={type.id}
                className={cn(
                  'p-4 flex items-center',
                  !type.isActive && 'bg-gray-50 opacity-60'
                )}
              >
                <div className="w-10 cursor-move text-gray-400 hover:text-gray-600">
                  <GripVertical className="h-5 w-5" />
                </div>

                <div className="flex-1 px-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">
                      {iconMap[type.icon || ''] || '📋'}
                    </span>
                    <div>
                      <h3 className="font-medium text-gray-900">{type.name}</h3>
                      <p className="text-sm text-gray-500">{type.description}</p>
                    </div>
                  </div>
                </div>

                <div className="w-32 text-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                    {type.enabledCount || 0} venues
                  </span>
                </div>

                <div className="w-24 flex justify-center">
                  <button
                    onClick={() =>
                      toggleMutation.mutate({ id: type.id, isActive: !type.isActive })
                    }
                    className={cn(
                      'flex items-center gap-1 text-sm font-medium',
                      type.isActive ? 'text-green-600' : 'text-gray-400'
                    )}
                  >
                    {type.isActive ? (
                      <>
                        <ToggleRight className="h-5 w-5" />
                        Active
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-5 w-5" />
                        Inactive
                      </>
                    )}
                  </button>
                </div>

                <div className="w-20 text-right">
                  <button className="p-1 rounded-md hover:bg-gray-100">
                    <Edit className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-6 card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Booking Type Configuration
        </h2>
        <div className="prose prose-sm text-gray-500">
          <p>
            Each booking type represents a different way venues can accept reservations:
          </p>
          <ul className="mt-2 space-y-1">
            <li><strong>Table Bookings:</strong> Restaurant reservations with rooms, tables, and time slots</li>
            <li><strong>Event Tickets:</strong> Concert and event ticket sales with multiple ticket types</li>
            <li><strong>Cinema Seats:</strong> Theatre seating with visual seat selection</li>
            <li><strong>Hotel Rooms:</strong> Accommodation bookings with seasonal pricing</li>
            <li><strong>Function Rooms:</strong> Meeting and event space hire</li>
            <li><strong>General Requests:</strong> Custom booking forms for anything else</li>
          </ul>
          <p className="mt-4">
            Venues can enable any combination of these booking types. Each type has its own configuration options that venue admins can customize.
          </p>
        </div>
      </div>
    </div>
  );
}
